import moment from "moment";
import React, { useState, useEffect, useCallback } from "react";
import { useRef } from "react";
import { CSVLink } from "react-csv";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getData } from "../../api";
import { importUserFromExcel } from "../../api/user";
import { createExportRequest, getCompanyExportRequestStatus, logExportAction } from "../../api";
import Loader from "../../component/Loader";
import DeleteModal from "../../component/Modal/Delete";
import FilterModal from "../../component/Modal/FilterModal";
import Paginations from "../../component/Pagination";
import ImportModal from "./ImportModal";
import { variants } from "../../constant/Variants";
import Select from "react-select";
import {
  FetchUserList,
  totalUserCountSelector,
  userListSelector,
} from "../user/UserSlice";

function ImportExportUser() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const list = useSelector(userListSelector);
  const total = useSelector(totalUserCountSelector);
  const authState = useSelector((store) => store.auth);
  const userDetails = useSelector((store) => store.subAdmin.detail);

  const parseBooleanFlag = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const normalized = value.toLowerCase();
      if (["1", "true", "yes"].includes(normalized)) return true;
      if (["0", "false", "no"].includes(normalized)) return false;
    }
    return null;
  };

  const resolvedIsCompany = parseBooleanFlag(userDetails?.is_company);
  const fallbackIsCompany = (authState?.user_type || "")?.toLowerCase() === "company";
  const isCompanyAdmin = resolvedIsCompany ?? fallbackIsCompany;
  const isSuperAdmin = resolvedIsCompany !== null
    ? !resolvedIsCompany
    : (authState?.admin_type || "")?.toLowerCase() === "super admin";
  const canImport = isSuperAdmin;
  const canExport = isSuperAdmin || isCompanyAdmin;
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [show, setShow] = useState(false);
  const [delId, setDelId] = useState("");
  const [modalName, setModalName] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [latestDisapprovedRequest, setLatestDisapprovedRequest] = useState(null);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  let companyOptions = [];
  const companiesStr = localStorage.getItem('companies');
  if (companiesStr) {
    try {
      const companies = JSON.parse(companiesStr);
      if (Array.isArray(companies) && companies.length > 0) {
        companyOptions = [
          { value: 0, label: 'All Companies' },
          ...companies.map(c => ({
            value: c.id,
            label: c.company_name
          }))
        ];
      }
    } catch (e) {
      // Optionally handle JSON parse error
      companyOptions = [];
    }
  }
  const [selectedCompany, setSelectedCompany] = useState(0);
  const csvLink = useRef();

  const handleShow = () => {
    setShow(!show);
  };

  const handlePage = (val) => {
    setPage(val);
  };

  const getList = useCallback(() => {
    setLoading(true);
    dispatch(FetchUserList({ page: page, limit: 10, search: search, company_id : selectedCompany })).then(
      () => {
        setLoading(false);
      }
    );
  }, [dispatch, page, search, selectedCompany]);

  useEffect(() => {
    getList();
  }, [getList]);

  useEffect(() => {
    if (isCompanyAdmin && userDetails?.id) {
      checkPendingRequest();
    }
  }, [isCompanyAdmin, userDetails?.id]);

  const checkPendingRequest = async () => {
    if (!userDetails?.id) return;
    
    const response = await getCompanyExportRequestStatus(userDetails.id, "user");
    if (response?.code === 1 && response?.data) {
      // Handle the new response structure
      if (response.data.hasPendingRequest) {
        setPendingRequest(response.data.pendingRequest);
      } else if (response.data.hasApprovedRequest) {
        setPendingRequest(response.data.approvedRequest);
      } else {
        // No active requests (pending or approved)
        setPendingRequest(null);
      }
      
      // Use latestRequest for disapproved message
      const latestRequest = response.data.latestRequest;
      if (latestRequest && latestRequest.status === 2) {
        setLatestDisapprovedRequest(latestRequest);
      } else {
        setLatestDisapprovedRequest(null);
      }
    } else {
      setPendingRequest(null);
      setLatestDisapprovedRequest(null);
    }
  };

  const handleDOwnloadCSV = async () => {
    if (isCompanyAdmin) {
      await handleCompanyExport();
    } else if (isSuperAdmin) {
      await handleSuperAdminExport();
    }
  };

  const handleCompanyExport = async () => {
    if (pendingRequest) {
      if (pendingRequest.status === 0) {
        toast.info("Your export request is pending for approval.");
        return;
      } else if (pendingRequest.status === 2) {
        //toast.error("Your export request has been disapproved. Please submit a new request.");
        //return;
      } else if (pendingRequest.status === 1) {
        // Request is approved, allow export
        await performExport();
        return;
      }
    }

    // No pending request, create new one
    if (!userDetails || !userDetails.id) {
      toast.error("User details not available. Please refresh the page.");
      return;
    }
    setLoadingRequest(true);
    const response = await createExportRequest(userDetails.id, 'user');
    setLoadingRequest(false);

    if (response?.code === 1) {
      toast.success("Your export request has been submitted for approval.");
      setPendingRequest(response.data);
      await checkPendingRequest();
    } else {
      toast.error(response?.message || "Failed to submit export request");
    }
  };

  const performExport = () => {
    setDownloading(true);
    dispatch(FetchUserList({ search: search, company_id : selectedCompany }))
      .then((res) => {
        let list = res?.payload?.data?.user_list;
        if (list?.length > 0) {
          let arr = list?.map((item) => {
            let data = {
              first_name: item?.first_name,
              last_name: item?.last_name,
              email: item?.email,
              username: item?.username,
              password: item?.password,
              property_number: item?.address_details?.property_number,
              property_name: item?.address_details?.property_name,
              street_number: item?.address_details?.street_number,
              town: item?.address_details?.town,
              country: item?.address_details?.country,
              postal_code: item?.address_details?.postal_code,
              mobile: item?.address_details?.mobile,
              countryCode: item?.address_details?.countryCode,
              dob: item?.address_details?.dob,
              dob_day: item?.address_details?.dob_day,
              dob_month: item?.address_details?.dob_month,
              dob_year: item?.address_details?.dob_year,
            };
            return data;
          });

          setCsvData(arr);
          
          // Log the export action to backend
          if (arr.length > 0) {
            logExportAction('user', arr.length, 'csv', selectedCompany).then((logRes) => {
              if (logRes?.code === 1) {
                console.log('Export logged successfully');
              } else {
                console.error('Failed to log export:', logRes?.message);
              }
            });
          }
        }
        return res;
      })
      .then((res) => {
        setDownloading(false);

        if (res?.payload?.code === 1) {
          // Clear disapproved message after successful export
          setLatestDisapprovedRequest(null);
          // Refresh request status to get updated state
          if (isCompanyAdmin && userDetails?.id) {
            checkPendingRequest();
          }
          
          if (csvLink.current) {
            csvLink.current.link.click();
          } else {
            console.error('CSVLink ref is not available');
          }
        }
      });
  };

  const handleSuperAdminExport = () => {
    setDownloading(true);
    dispatch(FetchUserList({ search: search, company_id : selectedCompany }))
      .then((res) => {
        setLoading(false);
        let list = res?.payload?.data?.user_list;
        if (list?.length > 0) {
          let arr = list?.map((item) => {
            let data = {
              first_name: item?.first_name,
              last_name: item?.last_name,
              email: item?.email,
              username: item?.username,
              password: item?.password,
              property_number: item?.address_details?.property_number,
              property_name: item?.address_details?.property_name,
              street_number: item?.address_details?.street_number,
              town: item?.address_details?.town,
              country: item?.address_details?.country,
              postal_code: item?.address_details?.postal_code,
              mobile: item?.address_details?.mobile,
              countryCode: item?.address_details?.countryCode,
              dob: item?.address_details?.dob,
              dob_day: item?.address_details?.dob_day,
              dob_month: item?.address_details?.dob_month,
              dob_year: item?.address_details?.dob_year,
            };
            return data;
          });

          setCsvData(arr);
          
          // Log the export action to backend
          if (arr.length > 0) {
            logExportAction('user', arr.length, 'csv', selectedCompany).then((logRes) => {
              if (logRes?.code === 1) {
                console.log('Export logged successfully');
              } else {
                console.error('Failed to log export:', logRes?.message);
              }
            });
          }
        }
        return res;
      })
      .then((res) => {
        setDownloading(false);

        if (res?.payload?.code === 1) {
          // Clear disapproved message after successful export
          setLatestDisapprovedRequest(null);
          // Refresh request status to get updated state
          if (isCompanyAdmin && userDetails?.id) {
            checkPendingRequest();
          }
          
          if (csvLink.current) {
            csvLink.current.link.click();
          } else {
            console.error('CSVLink ref is not available');
          }
        }
      });
  };

  useEffect(() => {}, [list]);

  const handleImportUser = (file, companyId) => {
    setImporting(true);
    importUserFromExcel({ file, company_id: companyId }).then((res) => {
      if (res?.code === 1) {
        toast.success(res?.message || 'Users imported successfully');
        getList();
        setShowImportModal(false);
      } else {
        // Show detailed error information
        let errorMessage = res?.message || res?.msg || 'Import failed';
        
        // If there's additional error data, show it
        if (res?.data?.error) {
          errorMessage += `: ${res.data.error}`;
        }
        
        toast.error(errorMessage, { autoClose: false, closeButton: true });
        console.error('Import error details:', res);
      }
    }).catch((error) => {
      // This should rarely be called now since postFormData handles errors
      let errorMessage = 'Import failed';
      if (error?.message) {
        errorMessage += `: ${error.message}`;
      }
      toast.error(errorMessage, { autoClose: false, closeButton: true });
      console.error('Import error:', error);
    }).finally(() => {
      setImporting(false);
    });
  };

  return (
    <article className="col-lg-9 articleClass">
      <div className="article-body store-bd-gy">
        <div className="row align-items-center col-rever">
          <div className="col-sm-12 col-md-6 col-lg-6 mb-3">
            <div className="link-active shadow">
              <p>
                <img src="assets/images/event-calender-selected.png" alt="" />{" "}
                {isCompanyAdmin ? "Data Export" : "Data Import / Export"} {total}
              </p>
            </div>
          </div>
          <div className="col-sm-12 col-md-6 col-lg-6 mb-3">
            <div className="d-flex align-items-center lg-justify-content-end">
              <form className="has-search">
                <span className="form-search-icon form-control-feedback"></span>
                <input
                  type="text"
                  className="form-control shadow"
                  placeholder="Search by name"
                  value={search}
                  onChange={(e) => {
                    setPage(1);

                    setSearch(e.target.value);
                  }}
                />
              </form>
              {canImport && (
                <button 
                  className="btn filter btn-accept px-3 fs-6 d-flex align-items-center"
                  onClick={() => setShowImportModal(true)}
                >
                  <img
                    src="assets/images/download.png"
                    alt=""
                    className="download-icon"
                  />{" "}
                  Import
                </button>
              )}
              <CSVLink
                data={csvData}
                asyncOnClick={true}
                filename={"user_list.csv"}
                className="d-none"
                ref={csvLink}
              ></CSVLink>
              {canExport && (
                <>
                  <button
                    className="btn filter btn-accept btn-view  px-3 fs-normal"
                    onClick={handleDOwnloadCSV}
                    disabled={loadingRequest || downloading || (isCompanyAdmin && pendingRequest && pendingRequest.status == 0)}
                  >
                    <img
                      src="assets/images/download.png"
                      alt=""
                      className="download-icon"
                    />
                    {loadingRequest ? "Submitting..." : 
                     downloading ? "Downloading..." :
                     pendingRequest?.status === 0 ? "Request Pending" :
                     (pendingRequest?.status === 1 || pendingRequest?.status === 2) ? "Export CSV" :
                     "Export CSV"}
                  </button>
                  
                  {isCompanyAdmin && (pendingRequest || latestDisapprovedRequest) && (
                    <small className={`d-block mt-1 ${
                      pendingRequest?.status === 0 ? "text-warning" :
                      pendingRequest?.status === 1 ? "text-success" :
                      latestDisapprovedRequest ? "text-danger" :
                      "text-danger"
                    }`}>
                      {pendingRequest?.status === 0 && "Your export request is pending for approval."}
                      {pendingRequest?.status === 1 && "Your export request has been approved. You can now export data."}
                      {latestDisapprovedRequest && "Your export request has been disapproved. Please submit a new request."}
                    </small>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        <div className="row mt-1">
          <div className="col-sm-9 col-md-9 col-lg-10">
            <ul
              className="nav nav-pills group-nav mb-3"
              id="pills-tab"
              role="tablist"
            >
              <li className="nav-item" role="presentation">
                <button
                  className="nav-link active"
                  id="pills-new-tab"
                  data-bs-toggle="pill"
                  data-bs-target="#pills-new"
                  type="button"
                  role="tab"
                  aria-controls="pills-new"
                  aria-selected="true"
                  onClick={() => {
                    navigate("/import-export-data/user");
                  }}
                >
                  Users
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className="nav-link"
                  id="pills-completed-tab"
                  data-bs-toggle="pill"
                  data-bs-target="#pills-completed"
                  type="button"
                  role="tab"
                  aria-controls="pills-completed"
                  aria-selected="false"
                  onClick={() => {
                    navigate("/import-export-data/student");
                  }}
                >
                  Students
                </button>
              </li>
              {isSuperAdmin && (
               <>
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="pills-requests-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-requests"
                    type="button"
                    role="tab"
                    aria-controls="pills-requests"
                    aria-selected="false"
                    onClick={() => {
                      navigate("/import-export-data/requests");
                    }}
                  >
                    Export Requests
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="pills-logs-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-logs"
                    type="button"
                    role="tab"
                    aria-controls="pills-logs"
                    aria-selected="false"
                    onClick={() => {
                      navigate("/import-export-data/logs");
                    }}
                  >
                    Logs
                  </button>
                </li>
              </>
              )}
            </ul>
          </div>
          {Array.isArray(companyOptions) && companyOptions.length > 0 && (
          <div className="col-sm-12 col-md-12 col-lg-4 mb-3">
            <Select
                options={companyOptions}
                isSearchable
                placeholder="Select a company..."
                defaultValue={companyOptions[selectedCompany]}
                onChange={option => {
                  setSelectedCompany(option.value);
                }}
              />
          </div>
          )}
          <div className="col-sm-12">
            <div className="table-responsive text-center">
              <table className="table storetable">
                <thead className="text-center">
                  <tr>
                    <th scope="col">ID</th>
                    <th scope="col">First Name</th>
                    <th scope="col">Last Name</th>
                    <th scope="col">User Name</th>
                    <th scope="col">Email ID</th>
                    <th scope="col">Date of Birth</th>
                    <th scope="col">Contact Number</th>
                    <th scope="col">Full Address</th>

                    {/* <th scope="col">action</th> */}
                  </tr>
                  <tr className="border-0 bg-none invisible">
                    <td className="border-0 bg-none"></td>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <td colSpan={9}>
                      <Loader />
                    </td>
                  ) : (
                    <>
                      {list?.length > 0 ? (
                        list?.map((item, i) => {
                          return (
                            <tr variants={variants.card} key={i}>
                              <td>{item?.id || "NA"}</td>
                              <td>
                                {item?.first_name ? item?.first_name : "NA"}
                              </td>
                              <td>
                                {item?.last_name ? item?.last_name : "NA"}
                              </td>
                              <td>{item?.username ? item?.username : "NA"}</td>
                              <td>{item?.email ? item?.email : "NA"}</td>
                              <td>
                                {item?.address_details?.dob &&
                                item?.address_details?.dob !== "0000-00-00"
                                  ? moment(item?.address_details?.dob).format(
                                      "DD MMM YYYY"
                                    )
                                  : item?.address_details?.dob_year
                                  ? `${moment(
                                      `${item?.address_details?.dob_year}-${item?.address_details?.dob_month}-${item?.address_details?.dob_day}`
                                    ).format("DD MMM YYYY")}`
                                  : "NA"}
                              </td>
                              <td>
                                {item?.address_details?.mobile
                                  ? `${
                                      item?.address_details?.countryCode
                                        ? item?.address_details?.countryCode
                                        : ""
                                    }${item?.address_details?.mobile}`
                                  : "NA"}
                              </td>
                              <td>
                                {item?.address_details
                                  ? `${
                                      item?.address_details?.property_number
                                        ? `${item?.address_details?.property_number}, `
                                        : ""
                                    }${
                                      item?.address_details?.property_name
                                        ? `${item?.address_details?.property_name}, `
                                        : ""
                                    }${
                                      item?.address_details?.street_number
                                        ? `${item?.address_details?.street_number}, `
                                        : ""
                                    }${
                                      item?.address_details?.town
                                        ? `${item?.address_details?.town}, `
                                        : ""
                                    }${
                                      item?.address_details?.country
                                        ? `${item?.address_details?.country}`
                                        : ""
                                    }${
                                      item?.address_details?.postal_code
                                        ? `-${item?.address_details?.postal_code}`
                                        : ""
                                    }`
                                  : "NA"}
                              </td>

                              {/* <td>
                                <div className="d-flex justify-content-center action">
                                  <button className="btn" title="View User">
                                    <Link to={`/user/${item?.id}`}>
                                      <img
                                        src="assets/images/view.png"
                                        alt=""
                                      />
                                    </Link>
                                  </button>
                                  <button className="btn" title="Edit User">
                                    <Link to={`/edit-user/${item?.id}`}>
                                      <img
                                        src="assets/images/edit.png"
                                        alt=""
                                      />
                                    </Link>
                                  </button>
                                  <button
                                    className="btn"
                                    onClick={() => {
                                      setDelId(item.id);
                                      handleShow();
                                    }}
                                    title="Delete User"
                                  >
                                    <img
                                      src="assets/images/delete.png"
                                      alt=""
                                    />
                                  </button>
                                  <button className="btn">
                                <input
                                  type="checkbox"
                                  className="toggle"
                                  checked=""
                                />
                              </button>
                                </div>
                              </td> */}
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={10}>No data found.</td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {list?.length > 0 && (
            <Paginations
              handlePage={handlePage}
              page={page}
              total={total && total}
            />
          )}
        </div>
      </div>
      {modalName === "filter" && (
        <FilterModal show={show} handleShow={handleShow} />
      )}
      {modalName === "delete" && (
        <DeleteModal
          page="Event"
          show={show}
          handleShow={handleShow}
          cancel={() => {
            setShow(false);
          }}
        />
      )}
      <ImportModal
        show={showImportModal}
        handleShow={() => setShowImportModal(false)}
        onImport={handleImportUser}
        loading={importing}
      />
    </article>
  );
}

export default ImportExportUser;
